// frontend/src/components/pantallaInformativa/colaDeVideos.js
// Qué video de la cola está pasando el TV, si está en pausa, y cómo se aplican los comandos del
// control remoto de Atención (el TV no tiene botones de video). Es un reducer puro para poder
// probarlo sin reproductor: la página (PantallaInformativaPage) lo usa con useReducer.
//
// Las acciones que mueven la cola reciben la lista de videos ({ id, url }) en `videos`.

export const ESTADO_INICIAL_COLA = {
    idActual: null,
    // Crece con cada salto: así una cola de un solo video vuelve a empezar al terminar.
    reproduccion: 0,
    pausado: false,
    // La secuencia del último comando remoto aplicado; null hasta que llega el primero.
    secuenciaControl: null,
    // Videos seguidos que YouTube no pudo pasar; si fallaron todos, el TV muestra el cartel.
    erroresSeguidos: 0,
    // Un video pedido desde Atención que todavía no llegó en la lista (se guardó recién).
    idPendiente: null,
};

function saltar(estado, videos, desplazamiento) {
    if (!videos?.length) return { ...estado, idActual: null };
    const actual = videos.findIndex((video) => video.id === estado.idActual);
    const desde = actual === -1 ? (desplazamiento > 0 ? -1 : 0) : actual;
    const indice = (desde + desplazamiento + videos.length) % videos.length;
    return { ...estado, idActual: videos[indice].id, reproduccion: estado.reproduccion + 1 };
}

function irA(estado, videos, id) {
    if (!videos?.some((video) => video.id === id)) return { ...estado, idPendiente: id ?? null };
    return {
        ...estado,
        idActual: id,
        idPendiente: null,
        reproduccion: estado.reproduccion + 1,
        pausado: false,
        erroresSeguidos: 0,
    };
}

/**
 * Un comando remoto se aplica una sola vez, cuando trae una secuencia mayor a la última: llega
 * repetido en cada latido y en cada actualización. El primero que llega solo marca desde dónde
 * contar (y si el video quedó en pausa): un comando viejo no hace saltar a un TV que se acaba de abrir.
 */
function aplicarControl(estado, videos, control) {
    if (!control) return estado;
    const secuencia = Number(control.secuencia) || 0;
    if (estado.secuenciaControl === null) {
        return { ...estado, secuenciaControl: secuencia, pausado: Boolean(control.pausado) };
    }
    if (secuencia <= estado.secuenciaControl) return estado;

    const conSecuencia = { ...estado, secuenciaControl: secuencia };
    switch (control.accion) {
        case 'PAUSAR':
            return { ...conSecuencia, pausado: true };
        case 'REANUDAR':
            return { ...conSecuencia, pausado: false };
        case 'SIGUIENTE':
            return reducirCola(conSecuencia, { tipo: 'SIGUIENTE', videos });
        case 'ANTERIOR':
            return reducirCola(conSecuencia, { tipo: 'ANTERIOR', videos });
        case 'REPRODUCIR':
            return irA(conSecuencia, videos, control.idVideo);
        default:
            return { ...conSecuencia, pausado: Boolean(control.pausado) };
    }
}

export function reducirCola(estado, accion) {
    const { videos } = accion;
    switch (accion.tipo) {
        // Llegó la lista (al abrir o porque se guardó otra): si el video actual sigue, sigue.
        case 'LISTA': {
            const lista = videos ?? [];
            if (estado.idPendiente != null && lista.some((video) => video.id === estado.idPendiente)) {
                return irA(estado, lista, estado.idPendiente);
            }
            if (lista.some((video) => video.id === estado.idActual)) {
                return estado.erroresSeguidos === 0 ? estado : { ...estado, erroresSeguidos: 0 };
            }
            return {
                ...estado,
                idActual: lista[0]?.id ?? null,
                reproduccion: estado.reproduccion + 1,
                erroresSeguidos: 0,
            };
        }
        // Saltar de video (pedido desde Atención) también lo reanuda.
        case 'SIGUIENTE':
            return { ...saltar(estado, videos, 1), pausado: false, erroresSeguidos: 0 };
        case 'ANTERIOR':
            return { ...saltar(estado, videos, -1), pausado: false, erroresSeguidos: 0 };
        case 'IR_A':
            return irA(estado, videos, accion.id);
        case 'TERMINO':
            return { ...saltar(estado, videos, 1), erroresSeguidos: 0 };
        case 'ERROR':
            return { ...saltar(estado, videos, 1), erroresSeguidos: estado.erroresSeguidos + 1 };
        case 'ARRANCO':
            return estado.erroresSeguidos === 0 ? estado : { ...estado, erroresSeguidos: 0 };
        case 'REINTENTAR':
            return { ...estado, erroresSeguidos: 0, reproduccion: estado.reproduccion + 1 };
        case 'CONTROL':
            return aplicarControl(estado, videos, accion.control);
        default:
            return estado;
    }
}
